import { watch, ref } from "vue"
import { SearchResult } from "../types"
import debounce from "lodash.debounce"
import Kitsu from "./kitsu"
import Anilist from "./anilist"
import MyAnimeList from "./myanimelist"
import LastFM from "./lastfm"
import VNDB from "./vndb"
import RAWG from "./rawg"
import IGDB from "./igdb"
import Tenrai from "./tenrai"
import { API, APIWithShowMore, isAPIRequestError, isAPIServerError } from "./api"
import { fileToDataUrl } from "../image/data-url"

const apis = [new Kitsu(), new MyAnimeList(), new Tenrai(), new Anilist(), new LastFM(), new RAWG(), new IGDB(), new VNDB()]
const apisMap = new Map<string, API<unknown>>()

apis.forEach(api => {
  apisMap.set(api.name, api)
})

const apiFromString = (name: string): API<unknown> => {
  const api = apisMap.get(name)
  if (!api) {
    throw Error("api not found")
  }
  return api
}

const normalizeApiName = (name: string): string => {
  return name === "Jikan" || name === "MyAnimeList" ? "MAL" : name
}
const storage_api = normalizeApiName(localStorage.getItem("api") || "Anilist")
const api_exists = apis.some(api => {return api.name == storage_api })
const used_api = api_exists ? storage_api : apis[0].name

let api: API<unknown> = apiFromString(used_api)
let lastQuery = ""
let skipNextQuerySearch = false

const apiNames = apis.map(api => api.name)
const currentApi = ref(used_api)
const query = ref("")
const currentTab = ref(api.tabs[0])
const loading = ref(false)
const results = ref<SearchResult[]>([])
const tabs = ref(api.tabs)
const has_show_more = ref(api.supportsShowMore(currentTab.value))
const showing_more = ref(false)
const selected_title = ref("")
const warning = ref("")
const issueUrl = ref("")
const serverErrorMessage = "This API is temporarily unavailable. Try again later or open an issue if the error persists."

const buildIssueUrl = ({ action, tab, query, error }: { action: string, tab: string, query: string, error: unknown }): string => {
  const errorDetails = error instanceof Error
    ? `${error.name}: ${error.message}\n\n${error.stack || "No stack trace available."}`
    : String(error)
  const params = new URLSearchParams({
    title: `Search error with ${currentApi.value}`,
    body: [
      "## Search Error",
      "",
      `API: ${currentApi.value}`,
      `Action: ${action}`,
      `Tab: ${tab}`,
      `Query: ${query || "(empty)"}`,
      "",
      "## Error",
      "",
      "```",
      errorDetails,
      "```"
    ].join("\n")
  })

  return `https://github.com/gqgs/3x3-generator/issues/new?${params.toString()}`
}

const showRequestWarning = ({ action, tab, query, error }: { action: string, tab: string, query: string, error: unknown }): void => {
  warning.value = serverErrorMessage
  issueUrl.value = buildIssueUrl({ action, tab, query, error })
}

const clearWarning = (): void => {
  warning.value = ""
  issueUrl.value = ""
}

const search = debounce(async (query: string, tab: string): Promise<void> => {
  loading.value = true
  showing_more.value = false
  selected_title.value = ""
  clearWarning()
  has_show_more.value = api.supportsShowMore(tab)
  try {
    results.value = await api.search(query, tab)
  } catch (error) {
    results.value = []
    if (isAPIServerError(error) || isAPIRequestError(error)) {
      showRequestWarning({ action: "search", tab, query, error })
    } else {
      console.error("Search failed:", error)
    }
  } finally {
    loading.value = false
  }
}, 500)

const changeTab = async (newtab: string): Promise<void> => {
  currentTab.value = newtab
  has_show_more.value = api.supportsShowMore(newtab)
  await search(lastQuery, newtab)
}

watch(currentApi, (api) => {
  localStorage.setItem("api", api)
})

watch(query, async (query) => {
  if (skipNextQuerySearch) {
    skipNextQuerySearch = false
    lastQuery = query
    return
  }
  lastQuery = query
  await search(query, currentTab.value)
})

const changeApi = async (newApi: string): Promise<void> => {
  currentApi.value = newApi
  api = apiFromString(newApi)
  tabs.value = api.tabs
  selected_title.value = ""
  if (!api.tabs.includes(currentTab.value)) {
    currentTab.value = api.tabs[0]
  }
  has_show_more.value = api.supportsShowMore(currentTab.value)
  await search(lastQuery, currentTab.value)
}

const showMore = async ({ tab, selected }: { tab: string, selected: SearchResult }): Promise<void> => {
  loading.value = true
  clearWarning()
  try {
    results.value = await (api as APIWithShowMore<unknown, unknown>).showMore({ tab, selected })
    showing_more.value = true
    selected_title.value = selected.title
  } catch (error) {
    results.value = []
    showing_more.value = false
    selected_title.value = ""
    if (isAPIServerError(error) || isAPIRequestError(error)) {
      showRequestWarning({ action: "show more", tab, query: selected.title, error })
    } else {
      console.error("Show more failed:", error)
    }
  } finally {
    loading.value = false
  }
}

const goBack = async (): Promise<void> => {
  await search(lastQuery, currentTab.value)
}

const handleFiles = async (files: FileList | File[] | null | undefined) => {
  if (!files) return
  search.cancel()
  const dropped: SearchResult[] = []
  for (let i = 0; i < files.length; i++) {
    const file = Array.isArray(files) ? files[i] : files.item(i)
    if (!file) continue
    const imageUrl = await fileToDataUrl(file)
    dropped.push({
      mal_id: Date.now() + i,
      title: file.name,
      image_url: imageUrl,
      sourceDataUrl: imageUrl,
    })
  }
  if (query.value !== "") {
    skipNextQuerySearch = true
    query.value = ""
  }
  lastQuery = ""
  results.value = dropped
  clearWarning()
  has_show_more.value = false
  showing_more.value = false
  selected_title.value = ""
}

const handleDrop = async (event: DragEvent) => {
  await handleFiles(event.dataTransfer?.files)
}

const handleFileInput = async (event: Event) => {
  const input = event.target as HTMLInputElement
  await handleFiles(input.files)
  input.value = ""
}

const getSearchState = () => {
  return {
    api: currentApi.value,
    tab: currentTab.value,
    query: query.value
  }
}

const restoreSearchState = ({ api: apiName, tab, query: restoredQuery }: { api: string, tab: string, query: string }) => {
  const normalizedApiName = normalizeApiName(apiName)
  const nextApi = apisMap.has(normalizedApiName) ? normalizedApiName : apis[0].name
  currentApi.value = nextApi
  api = apiFromString(nextApi)
  tabs.value = api.tabs
  currentTab.value = api.tabs.includes(tab) ? tab : api.tabs[0]
  has_show_more.value = api.supportsShowMore(currentTab.value)
  search.cancel()
  const nextQuery = restoredQuery || ""
  if (query.value !== nextQuery) {
    skipNextQuerySearch = true
    query.value = nextQuery
  }
  lastQuery = query.value
  results.value = []
  clearWarning()
  showing_more.value = false
  selected_title.value = ""
  localStorage.setItem("api", currentApi.value)
}

export default {
  apis,
  apiNames,
  currentApi,
  changeApi,
  tabs,
  currentTab,
  changeTab,
  loading,
  query,
  results,
  warning,
  issueUrl,
  has_show_more,
  showing_more,
  selected_title,
  showMore,
  goBack,
  handleDrop,
  handleFileInput,
  getSearchState,
  restoreSearchState
}
